import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

// Loading Spinner Component
const LoadingSpinner = ({ className }: { className?: string }) => (
  <svg
    className={cn("animate-spin", className)}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

const buttonVariants = cva(
  "cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap text-foreground font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none  [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        solid: "",
        bordered: "border",
        light: "bg-transparent border-transparent",
        faded: "border-none",
        shadow: "shadow",
        ghost: "border",
        flat: "border",
      },
      colors: {
        default: "",
        primary: "",
        secondary: "",
        white: "",
        accent: "",
      },
      radius: {
        xs: "rounded-xs",
        sm: "rounded-xs",
        md: "rounded-xs",
        lg: "rounded-xs",
        xl: "rounded-xs",
        full: "rounded-full",
      },
      size: {
        sm: "h-6 px-3 text-xs",
        md: "h-8 px-4 text-sm",
        lg: "h-10 px-6 text-base",
        // Pixel-stable sizes for compact controls that must not expand when a
        // browser changes its default root font size. Browser zoom still
        // scales CSS pixels normally.
        "stable-sm": "h-[24px] px-[10px] text-[11px]",
        "stable-md": "h-[34px] px-[12px] text-[13px]",
        "stable-lg": "h-[40px] px-[20px] text-[14px]",
        "stable-icon": "size-[32px] p-0",
        icon: "p-2",
      },
    },

    compoundVariants: [
      // Solid
      {
        variant: "solid",
        colors: "default",
        className:
          "bg-bg-tertiary hover:bg-bg-tertiary/90 disabled:bg-bg-tertiary/50",
      },
      {
        variant: "solid",
        colors: "secondary",
        className:
          "bg-surface hover:bg-surface/90 text-surface-foreground disabled:bg-surface/60 disabled:text-surface-foreground",
      },
      {
        variant: "solid",
        colors: "primary",
        className:
          "bg-accent hover:bg-accent/90 text-accent-foreground disabled:bg-accent/50",
      },
      {
        variant: "solid",
        colors: "white",
        className:
          "bg-white hover:bg-white/90 text-zinc-950 disabled:bg-white/50 disabled:text-zinc-950/40",
      },

      {
        variant: "solid",
        colors: "accent",
        className:
          "bg-accent hover:bg-accent/90 text-accent-foreground disabled:bg-stroke-light/40 disabled:text-text-disabled disabled:border disabled:border-stroke-light",
      },

      // Bordered
      {
        variant: "bordered",
        colors: "default",
        className:
          "border-stroke-primary hover:bg-bg-tertiary/90 disabled:border-stroke-primary/50",
      },
      {
        variant: "bordered",
        colors: "secondary",
        className:
          "border-surface hover:border-surface/80 text-foreground disabled:border-surface/50",
      },
      {
        variant: "bordered",
        colors: "primary",
        className:
          "border-accent hover:bg-accent/20 text-accent disabled:border-accent/50",
      },

      // Light
      {
        variant: "light",
        colors: "default",
        className: "text-foreground",
      },
      {
        variant: "light",
        colors: "primary",
        className: "text-accent",
      },

      // Flat
      {
        variant: "flat",
        colors: "default",
        className: "bg-bg-tertiary",
      },
      {
        variant: "flat",
        colors: "secondary",
        className: "bg-bg-tertiary border border-stroke-light text-muted",
      },
      {
        variant: "flat",
        colors: "primary",
        className: "bg-accent/20 text-accent",
      },

      // Faded
      {
        variant: "faded",
        colors: "default",
        className: "bg-bg-tertiary border-stroke-primary",
      },
      {
        variant: "faded",
        colors: "secondary",
        className: "bg-bg-tertiary/60",
      },
      {
        variant: "faded",
        colors: "primary",
        className: "bg-bg-tertiary border-stroke-primary text-accent",
      },

      // Shadow
      {
        variant: "shadow",
        colors: "default",
        className: "shadow-lg shadow-black/10 bg-bg-tertiary",
      },
      {
        variant: "shadow",
        colors: "secondary",
        className:
          "shadow-lg shadow-white/40 bg-surface hover:bg-surface/90 text-surface-foreground",
      },
      {
        variant: "shadow",
        colors: "primary",
        className:
          "shadow-lg shadow-accent/40 bg-accent text-accent-foreground",
      },

      // Ghost
      {
        variant: "ghost",
        colors: "default",
        className: "bg-transparent border-stroke-primary",
      },
      {
        variant: "ghost",
        colors: "secondary",
        className: "bg-transparent border-muted text-muted",
      },
      {
        variant: "ghost",
        colors: "primary",
        className: "bg-transparent border-accent text-accent",
      },
    ],
    defaultVariants: {
      variant: "solid",
      colors: "default",
      size: "md",
      radius: "sm",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  loading?: boolean;
  loadingText?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      colors,
      radius,
      asChild = false,
      startIcon,
      endIcon,
      loading = false,
      loadingText,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    // Pull `disabled` out of props so the spread below can't quietly override
    // the loading-derived isDisabled. Without this, a caller passing
    // disabled={false} alongside loading={true} would re-enable the button
    // mid-mutation (rage-click city).
    const { disabled: rawDisabled, ...restProps } = props;
    const isDisabled = rawDisabled || loading;

    const buttonProps = {
      className: cn(
        buttonVariants({ variant, size, colors, radius, className }),
      ),
      ref,
      ...restProps,
      disabled: isDisabled,
      "aria-disabled": isDisabled,
      ...(loading ? { "aria-busy": "true" as const } : {}),
    };

    const content = loading ? (
      <>
        <LoadingSpinner className="w-4 h-4" />
        {loadingText ? <span>{loadingText}</span> : null}
      </>
    ) : (
      <>
        {startIcon && (
          <span className="flex items-center justify-center">{startIcon}</span>
        )}
        {children}
        {endIcon && (
          <span className="flex items-center justify-center">{endIcon}</span>
        )}
      </>
    );

    if (asChild) {
      return <Slot {...buttonProps}>{children}</Slot>;
    }

    return <Comp {...buttonProps}>{content}</Comp>;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
